import { TAGS_FIELD_MAPPING_TARGET, TAG_VALUE_DELIMITER } from '../../core/constants';

/**
 * Client-side resolution of a CSV tags column to tag IDs.
 *
 * Doing this in the browser is what keeps the backend simple: it only ever
 * receives `{ csvValue: tagId }`, so there is no auto-create, no delimiter
 * handling, no unknown-name row validation, and no way for a typo to become a
 * permanent tag. See Phase 3 in .kiro/specs/contact-grouping/spec.md.
 */

/** Which CSV header, if any, is the tags column. */
export const findTagsHeader = (csvHeaders, fieldMappings) => {
  const explicit = Object.keys(fieldMappings).find((header) => fieldMappings[header] === TAGS_FIELD_MAPPING_TARGET);
  if (explicit) return explicit;

  // A column literally headed `tags` is treated as the tags column without an
  // explicit mapping — matching what the backend's mapFieldName does — but only
  // when the user has not mapped it somewhere else.
  return csvHeaders.find((header) => /^tags$/i.test(header.trim()) && !fieldMappings[header]) ?? '';
};

/** Split one cell into tag values. Semicolon: a tag name may contain a comma. */
export const splitTagCell = (cell) => {
  if (typeof cell !== 'string') return [];
  return cell
    .split(TAG_VALUE_DELIMITER)
    .map((value) => value.trim())
    .filter(Boolean);
};

/**
 * Distinct values in the tags column, with the number of rows carrying each, plus
 * how many rows have no value at all.
 *
 * Row counts matter: they are how a user judges whether an unrecognised value is
 * worth creating a tag for or is a stray typo in three rows.
 */
export const collectTagValues = (rows, tagsHeader) => {
  if (!tagsHeader) return { values: [], rowsWithNoValue: 0 };

  const counts = new Map();
  let rowsWithNoValue = 0;

  for (const row of rows) {
    const values = splitTagCell(row[tagsHeader]);
    if (!values.length) {
      rowsWithNoValue += 1;
      continue;
    }
    // A value repeated within one cell counts that row once.
    for (const value of new Set(values)) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }

  const values = [...counts.entries()]
    .map(([value, rowCount]) => ({ value, rowCount }))
    .sort((a, b) => b.rowCount - a.rowCount || a.value.localeCompare(b.value));

  return { values, rowsWithNoValue };
};

const normalize = (value) => value.trim().toLowerCase();

/**
 * Pre-match values to existing tags by case-insensitive trimmed name.
 *
 * Returns the auto-matched pairs and the values still needing a decision, which is
 * exactly the split option A2 renders: matched collapse into one line, undecided
 * get a card each.
 */
export const matchTagValues = (values, tags) => {
  const byName = new Map(tags.map((tag) => [normalize(tag.name), tag]));

  const matched = [];
  const unmatched = [];

  for (const entry of values) {
    const tag = byName.get(normalize(entry.value));
    if (tag) matched.push({ ...entry, tag });
    else unmatched.push(entry);
  }

  return { matched, unmatched };
};

/**
 * The payload field: `{ csvValue: tagId }`.
 *
 * Values the user ignored are absent rather than present-and-null, so the backend
 * has nothing to interpret — an absent value contributes no tag.
 */
export const buildTagValueMap = ({ matched, decisions }) => {
  const map = {};

  for (const { value, tag } of matched) {
    map[value] = tag.id;
  }

  for (const [value, decision] of Object.entries(decisions)) {
    if (decision?.tagId) map[value] = decision.tagId;
    else delete map[value];
  }

  return map;
};

/** True when every unmatched value has been decided (mapped, created, or ignored). */
export const allValuesResolved = (unmatched, decisions) =>
  unmatched.every(({ value }) => {
    const decision = decisions[value];
    return decision?.tagId || decision?.ignored;
  });
